import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Scale, Clock } from 'lucide-react'
import clsx from 'clsx'
import { getWeighingTrend } from '../../services/api'
import PeriodToggle from './PeriodToggle'

// Owner-view plan, Session 2.3 — avg weight per weighing session over the
// selected window + a "last weighed N days ago" pill. Pill turns amber after
// 30 days and red after 60 days — matches the _check_weighings_silent
// detector thresholds in anomaly_detector.py.
// S3b (B4): the all-animals day mean is class-mix noise under a rotating
// weekly sample, so the chart is stratified per class; the dashed gray
// "session average" line stays only as context.

const PERIOD_OPTIONS = [
  { value: 90, label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '365d' },
]

const CLASS_COLORS = ['#b45309', '#0e7490', '#4d7c0f', '#7c3aed']

function staleness(days) {
  if (days == null) return { label: 'No weighings recorded', tone: 'red' }
  if (days <= 1) return { label: 'Weighed today', tone: 'green' }
  if (days < 30) return { label: `Last weighed ${days}d ago`, tone: 'green' }
  if (days < 60) return { label: `Last weighed ${days}d ago`, tone: 'amber' }
  return { label: `Last weighed ${days}d ago`, tone: 'red' }
}

const TONE_CLASSES = {
  green: 'bg-green-50 text-green-700 ring-green-200',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
}

export default function WeighingTrendCard({ ranchId }) {
  const storageKey = `dashboard.weighingTrend.days.${ranchId}`
  const [days, setDays] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem(storageKey) || '', 10)
      return [90, 180, 365].includes(stored) ? stored : 90
    } catch { return 90 }
  })

  const setAndPersist = (next) => {
    setDays(next)
    try { localStorage.setItem(storageKey, String(next)) } catch { /* localStorage may be disabled (e.g. private mode) */ }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['weighingTrend', ranchId, days],
    queryFn: ({ signal }) => getWeighingTrend(ranchId, { days, signal }),
    staleTime: 60_000,
  })

  const { chartData, topClasses } = useMemo(() => {
    if (!data) return { chartData: [], topClasses: [] }
    // The classes with the most weighings in the window get their own line.
    const counts = {}
    for (const s of data.sessions || []) {
      for (const [cat, v] of Object.entries(s.by_category || {})) {
        counts[cat] = (counts[cat] || 0) + (v.n_animals || 0)
      }
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])
      .slice(0, 4).map(([cat]) => cat)
    const rows = (data.sessions || []).map((s) => {
      const row = {
        ...s,
        label: new Date(s.event_date).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
      }
      for (const cat of top) row[cat] = s.by_category?.[cat]?.avg_weight_kg ?? null
      return row
    })
    return { chartData: rows, topClasses: top }
  }, [data])

  const stale = staleness(data?.last_weighing_days_ago)

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Scale className="h-5 w-5 text-amber-600" />
          Weighing Trend
          <span className="text-xs font-normal text-gray-500">per class — session averages, mix varies</span>
        </h3>
        <div className="flex items-center gap-2">
          <span className={clsx(
            'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full ring-1',
            TONE_CLASSES[stale.tone]
          )}>
            <Clock className="h-3 w-3" />
            {stale.label}
          </span>
          <PeriodToggle
            ariaLabel="Weighing trend period"
            options={PERIOD_OPTIONS}
            value={days}
            onChange={setAndPersist}
          />
        </div>
      </div>

      {isLoading || !data ? (
        <div className="h-56 animate-pulse bg-gray-50 rounded" />
      ) : chartData.length === 0 ? (
        <div className="h-56 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Scale className="h-10 w-10 mb-2 opacity-30" />
          <p>No weighings in the last {days} days.</p>
        </div>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}kg`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  const p = payload[0].payload
                  return (
                    <div className="bg-white p-2.5 shadow-lg rounded border border-gray-200 text-xs">
                      <p className="text-gray-500">{label}</p>
                      {topClasses.map((cat) => (
                        p.by_category?.[cat] ? (
                          <p key={cat} className="text-gray-900">
                            <span className="capitalize">{cat}</span>:{' '}
                            <span className="font-semibold">{p.by_category[cat].avg_weight_kg} kg</span>
                            {' '}({p.by_category[cat].n_animals})
                          </p>
                        ) : null
                      ))}
                      <p className="text-gray-500 mt-1">
                        Session avg {p.avg_weight_kg} kg · {p.n_animals} animal{p.n_animals === 1 ? '' : 's'}
                      </p>
                    </div>
                  )
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (
                v === 'avg_weight_kg' ? 'session avg' : v
              )} />
              {topClasses.map((cat, i) => (
                <Line
                  key={cat}
                  type="monotone"
                  dataKey={cat}
                  connectNulls
                  stroke={CLASS_COLORS[i % CLASS_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              ))}
              <Line
                type="monotone"
                dataKey="avg_weight_kg"
                stroke="#9ca3af"
                strokeDasharray="4 3"
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
